import { useState } from 'react';

import {
  TreeSpeciesApiError,
  type TreeSpecies,
  type TreeSpeciesInput,
} from '../../../api/tree-species';
import { SimpleModal } from '../../shared/SimpleModal';

import TreeSpeciesField from './TreeSpeciesField';

interface Props {
  /** The species being edited, or null to add a new one. */
  species: TreeSpecies | null;
  onClose: () => void;
  onSave: (input: TreeSpeciesInput) => Promise<unknown>;
}

export default function TreeSpeciesFormModal({ species, onClose, onSave }: Props) {
  const [name, setName] = useState(species?.name ?? '');
  const [latinName, setLatinName] = useState(species?.latinName ?? '');
  const [description, setDescription] = useState(species?.description ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setFieldErrors({ name: 'Common name is required' });
      return;
    }
    setSaving(true);
    setFieldErrors({});
    try {
      await onSave({ name, latinName, description });
      onClose();
    } catch (err) {
      if (err instanceof TreeSpeciesApiError && Object.keys(err.fieldErrors).length > 0) {
        setFieldErrors(err.fieldErrors);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save the tree species.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <SimpleModal open onClose={onClose} title={species ? 'Edit tree species' : 'Add tree species'}>
      <form className="flex flex-col gap-3" onSubmit={handleSubmit} noValidate>
        {error && (
          <div role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
        <TreeSpeciesField
          label="Common name"
          value={name}
          onChange={setName}
          maxLength={100}
          error={fieldErrors.name}
          autoFocus
        />
        <TreeSpeciesField
          label="Latin name"
          value={latinName}
          onChange={setLatinName}
          maxLength={100}
          error={fieldErrors.latinName}
          optional
        />
        <TreeSpeciesField
          label="Tree description"
          value={description}
          onChange={setDescription}
          maxLength={5000}
          error={fieldErrors.description}
          optional
          multiline
        />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="nb-btn nb-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="nb-btn nb-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : species ? 'Save' : 'Add species'}
          </button>
        </div>
      </form>
    </SimpleModal>
  );
}
